// Lista de boletos virtuales de una venta con botones para descargar cada uno
// como PNG, descargar todos e imprimir. La usan la página pública (/sorteo?t=)
// y el detalle de la venta en el admin.
import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { Box, Stack, Button, CircularProgress, Typography } from '@mui/material';
import RaffleTicket, { downloadTicketPng, TICKET_W } from './RaffleTicket';

export default function TicketGallery({ raffle, numbers, buyer, raffleName = '' }) {
  const refs = useRef({});
  const [busy, setBusy] = useState(null);
  const [scale, setScale] = useState(1);
  const boxRef = useRef(null);

  // Escala el boleto al ancho disponible (teléfono) sin perder el tamaño real al exportar.
  useEffect(() => {
    const fit = () => { if (boxRef.current) setScale(Math.min(1, boxRef.current.clientWidth / TICKET_W)); };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  const slug = (raffleName || 'sorteo').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const downloadOne = async (n) => {
    setBusy(n);
    try { await downloadTicketPng(refs.current[n], `boleto-${slug}-${n}.png`); }
    catch (e) { console.error(e); window.alert('No se pudo generar la imagen. Intenta de nuevo o usa Imprimir.'); }
    finally { setBusy(null); }
  };
  const downloadAll = async () => {
    setBusy('all');
    try {
      // Uno por uno; el navegador los descarga en secuencia.
      // eslint-disable-next-line no-restricted-syntax
      for (const n of numbers) {
        // eslint-disable-next-line no-await-in-loop
        await downloadTicketPng(refs.current[n], `boleto-${slug}-${n}.png`);
      }
    } catch (e) { console.error(e); window.alert('No se pudo generar alguna imagen.'); }
    finally { setBusy(null); }
  };

  return (
    <Box ref={boxRef} className="ticket-gallery">
      <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap" className="no-print">
        {numbers.length > 1 && <Button size="small" variant="outlined" onClick={downloadAll} disabled={busy !== null}>{busy === 'all' ? <CircularProgress size={16} /> : 'Descargar todos (PNG)'}</Button>}
        <Button size="small" variant="outlined" onClick={() => window.print()}>Imprimir</Button>
      </Stack>
      <Stack spacing={2}>
        {numbers.map((n) => (
          <Box key={n} className="ticket-item">
            <RaffleTicket ref={(el) => { refs.current[n] = el; }} raffle={raffle} number={n} buyer={buyer} scale={scale} />
            <Stack direction="row" spacing={1} mt={0.75} alignItems="center" className="no-print">
              <Button size="small" onClick={() => downloadOne(n)} disabled={busy !== null}>{busy === n ? <CircularProgress size={14} /> : `Descargar boleto #${n}`}</Button>
              <Typography variant="caption" color="text.secondary">PNG listo para WhatsApp o imprimir</Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
      <style>{`@media print { .no-print { display: none !important; } .ticket-item { page-break-inside: avoid; margin-bottom: 12px; } body { background: #fff !important; } }`}</style>
    </Box>
  );
}

TicketGallery.propTypes = {
  raffle: PropTypes.object,
  numbers: PropTypes.array.isRequired,
  buyer: PropTypes.object,
  raffleName: PropTypes.string,
};
